/**
 * Reusable schema.org JSON-LD builders for SEO + AEO.
 * Each function returns a plain object ready for <JsonLd data={...} />.
 */

const BASE = "https://queebi.com";

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Queebi",
    alternateName: "Queebi Land",
    url: BASE,
    inLanguage: ["en", "zh-TW", "ja"],
    publisher: {
      "@type": "Organization",
      name: "BabyLux Co., Ltd.",
      url: BASE,
    },
  };
}

export function breadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

export function faqSchema(faqs: { question: string; answer: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.answer,
      },
    })),
  };
}

export function personSchema(args: {
  name: string;
  description: string;
  image: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    name: args.name,
    description: args.description,
    image: args.image.startsWith("http") ? args.image : `${BASE}${args.image}`,
    url: args.url,
    affiliation: {
      "@type": "Organization",
      name: "Queebi Land",
      url: BASE,
    },
  };
}

export function itemListSchema(args: {
  name: string;
  url: string;
  items: { name: string; url: string; image?: string; description?: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: args.name,
    url: args.url,
    numberOfItems: args.items.length,
    itemListElement: args.items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: it.url,
      name: it.name,
      ...(it.image && { image: it.image.startsWith("http") ? it.image : `${BASE}${it.image}` }),
      ...(it.description && { description: it.description }),
    })),
  };
}

export function audioBookSchema(args: {
  name: string;
  description: string;
  url: string;
  image?: string;
  inLanguage?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "AudioBook",
    name: args.name,
    description: args.description,
    url: args.url,
    inLanguage: args.inLanguage || "en",
    ...(args.image && { image: args.image.startsWith("http") ? args.image : `${BASE}${args.image}` }),
    publisher: {
      "@type": "Organization",
      name: "Queebi Land",
      url: BASE,
    },
    isAccessibleForFree: true,
  };
}

export function collectionPageSchema(args: {
  name: string;
  description: string;
  url: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: args.name,
    description: args.description,
    url: args.url,
    isPartOf: {
      "@type": "WebSite",
      name: "Queebi Land",
      url: BASE,
    },
  };
}

export function contactPageSchema(args: {
  name: string;
  description: string;
  url: string;
  email: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: args.name,
    description: args.description,
    url: args.url,
    mainEntity: {
      "@type": "Organization",
      name: "Queebi Land",
      email: args.email,
      url: BASE,
    },
  };
}

export function productSchema(args: {
  name: string;
  description: string;
  url: string;
  sku: string;
  mpn?: string;
  brand?: string;
  manufacturer?: string;
  images: string[];
  price: number;
  priceCurrency?: string;
  availability?: string;
  category?: string;
  offerUrl?: string;
  audience?: { suggestedMinAge?: number; audienceType?: string };
  additionalProperty?: { name: string; value: string }[];
}) {
  const today = new Date();
  const priceValidUntil = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate())
    .toISOString()
    .slice(0, 10);
  const absImages = args.images.map((i) => (i.startsWith("http") ? i : `${BASE}${i}`));
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: args.name,
    description: args.description,
    sku: args.sku,
    mpn: args.mpn || args.sku,
    image: absImages,
    url: args.url,
    brand: { "@type": "Brand", name: args.brand || "Queebi" },
    manufacturer: {
      "@type": "Organization",
      name: args.manufacturer || "BabyLux Co., Ltd.",
      url: BASE,
    },
    ...(args.category && { category: args.category }),
    ...(args.audience && {
      audience: {
        "@type": "PeopleAudience",
        ...(args.audience.suggestedMinAge !== undefined && { suggestedMinAge: args.audience.suggestedMinAge }),
        ...(args.audience.audienceType && { audienceType: args.audience.audienceType }),
      },
    }),
    ...(args.additionalProperty && {
      additionalProperty: args.additionalProperty.map((p) => ({
        "@type": "PropertyValue",
        name: p.name,
        value: p.value,
      })),
    }),
    offers: {
      "@type": "Offer",
      url: args.offerUrl || args.url,
      price: args.price.toFixed(2),
      priceCurrency: args.priceCurrency || "USD",
      priceValidUntil,
      itemCondition: "https://schema.org/NewCondition",
      availability: args.availability || "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "Queebi" },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 30,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "US",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
          transitTime: { "@type": "QuantitativeValue", minValue: 2, maxValue: 5, unitCode: "DAY" },
        },
      },
    },
  };
}
